import React, { useCallback, useEffect, useMemo, useState } from 'react';

const asArray = (data) => (Array.isArray(data) ? data : data ? [data] : []);
const emptyEvent = () => ({ title: '', date: '', time: '', location: '', rsvp: '', description: '' });
const deepClone = (o) => JSON.parse(JSON.stringify(o));

export default function EventsAdmin() {
  const [events, setEvents] = useState([]);
  const [original, setOriginal] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [draft, setDraft] = useState(emptyEvent());
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const isDirty = useMemo(() => JSON.stringify(original) !== JSON.stringify(events), [original, events]);

  const now = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const isUpcoming = useCallback((e) => new Date(e.date) >= now, [now]);

  const load = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/events');
      if (!res.ok) throw new Error(await res.text());
      const data = asArray(await res.json());
      setEvents(deepClone(data));
      setOriginal(deepClone(data));
    } catch (e) {
      setError(e.message || 'Failed to load events.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const publish = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(events),
      });
      if (!res.ok) throw new Error(await res.text());
      const saved = asArray(await res.json());
      setEvents(deepClone(saved));
      setOriginal(deepClone(saved));
      setMessage('Published successfully.');
    } catch (e) {
      setError(e.message || 'Failed to publish.');
    } finally {
      setSaving(false);
    }
  };

  const validate = () => {
    if (!draft.title.trim()) return 'Title is required.';
    if (!draft.date.trim()) return 'Date is required (YYYY-MM-DD).';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.date.trim())) return 'Date must be YYYY-MM-DD.';
    if (draft.rsvp && draft.rsvp.trim() && !/^https?:\/\//i.test(draft.rsvp.trim()))
      return 'RSVP link must start with http:// or https://';
    return '';
  };

  const saveDraft = () => {
    const err = validate();
    if (err) { alert(err); return; }
    if (editingIndex === -1) {
      setEvents((prev) => [...prev, { ...draft }]);
    } else {
      setEvents((prev) => prev.map((e, i) => i === editingIndex ? { ...draft } : e));
    }
    setEditingIndex(null);
    setDraft(emptyEvent());
  };

  const deleteEvent = (idx) => {
    if (!window.confirm(`Delete "${events[idx].title}" on ${events[idx].date}?`)) return;
    setEvents((prev) => prev.filter((_, i) => i !== idx));
    if (editingIndex === idx) { setEditingIndex(null); setDraft(emptyEvent()); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events
      .filter((e) => {
        if (filter === 'upcoming') return isUpcoming(e);
        if (filter === 'past') return !isUpcoming(e);
        return true;
      })
      .filter((e) => !q || `${e.title} ${e.location} ${e.description}`.toLowerCase().includes(q))
      .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  }, [events, filter, query, isUpcoming]);

  if (loading) return <div style={{ padding: 20 }}>Loading…</div>;

  return (
    <div>
      <div className="admin-header">
        <div>
          <h2>Events</h2>
          <p>Manage events shown on the public Events page.</p>
        </div>
        <div className="admin-actions">
          <button className="btn btn-outline btn-sm" onClick={load} disabled={saving}>Reload</button>
          <button className="btn btn-outline btn-sm" onClick={() => { setEditingIndex(-1); setDraft(emptyEvent()); }} disabled={saving}>+ Add event</button>
          <button className="btn btn-primary btn-sm" onClick={publish} disabled={!isDirty || saving}>
            {saving ? 'Publishing…' : 'Publish changes'}
          </button>
        </div>
      </div>

      {error && <div className="admin-msg-error">{error}</div>}
      {message && <div className="admin-msg-success">{message}</div>}

      {editingIndex !== null && (
        <div className="admin-form">
          <h3>{editingIndex === -1 ? 'Add event' : 'Edit event'}</h3>
          <div className="admin-form-grid">
            <label className="full-width">
              <span>Title *</span>
              <input value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} />
            </label>
            <label>
              <span>Date (YYYY-MM-DD) *</span>
              <input value={draft.date} onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))} placeholder="2025-08-10" />
            </label>
            <label>
              <span>Time</span>
              <input value={draft.time} onChange={(e) => setDraft((d) => ({ ...d, time: e.target.value }))} placeholder="12:00 PM – 4:00 PM" />
            </label>
            <label>
              <span>Location</span>
              <input value={draft.location} onChange={(e) => setDraft((d) => ({ ...d, location: e.target.value }))} />
            </label>
            <label>
              <span>RSVP link</span>
              <input value={draft.rsvp} onChange={(e) => setDraft((d) => ({ ...d, rsvp: e.target.value }))} placeholder="https://…" />
            </label>
            <label className="full-width">
              <span>Description</span>
              <textarea rows={4} value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} />
            </label>
          </div>
          <div className="admin-form-actions">
            <button className="btn btn-outline btn-sm" onClick={() => { setEditingIndex(null); setDraft(emptyEvent()); }}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={saveDraft}>Save</button>
          </div>
        </div>
      )}

      <div className="admin-filters">
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">All</option>
          <option value="upcoming">Upcoming</option>
          <option value="past">Past</option>
        </select>
        <input placeholder="Search title, location, description…" value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>

      <div className="admin-list">
        {filtered.length === 0
          ? <p style={{ color: 'var(--text-muted)' }}>No events found.</p>
          : filtered.map((e) => {
              const masterIdx = events.indexOf(e);
              return (
                <div className="admin-item" key={`${e.title}-${e.date}-${masterIdx}`}>
                  <div className="admin-item-info">
                    <div className="admin-item-title">{e.title || '(Untitled)'}</div>
                    <div className="admin-item-meta">
                      {e.date && <span>{e.date}</span>}
                      {e.time && <span>{e.time}</span>}
                      {e.location && <span>{e.location}</span>}
                      <span className="badge">{isUpcoming(e) ? 'upcoming' : 'past'}</span>
                      {e.rsvp && <a href={e.rsvp} target="_blank" rel="noreferrer" style={{ fontSize: '0.8rem' }}>RSVP link</a>}
                    </div>
                    {e.description && <div className="admin-item-body">{e.description.slice(0, 120)}{e.description.length > 120 ? '…' : ''}</div>}
                  </div>
                  <div className="admin-item-btns">
                    <button className="btn btn-outline btn-sm" onClick={() => { setEditingIndex(masterIdx); setDraft({ ...e }); }}>Edit</button>
                    <button className="btn btn-outline btn-sm" onClick={() => deleteEvent(masterIdx)}>Delete</button>
                  </div>
                </div>
              );
            })
        }
      </div>
    </div>
  );
}
